import * as actions from './actions';

import { catchError, exhaustMap, filter, map, mergeMap, switchMap, timeout, withLatestFrom } from 'rxjs/operators';

import { Epic } from 'redux-observable';
import { RootAction } from 'store/actions';
import { RootDependencies } from 'store/dependencies';
import { RootState } from 'store/reducer';
import { isActionOf } from 'typesafe-actions';
import { Observable, defer, forkJoin, of } from 'rxjs';
import { AddOnQuantityRange, AddOnRangeCheck, OwnerForm, ResolveAddOnFormResult } from './types';
import { findQuantityViolations } from './quantity-bounds';
import { transformErrorIntoSerializableError } from 'store/utils';

/* work-orders' owner-forms DTO: `@ArrayNotEmpty()` and `@ArrayMaxSize(100)`, both 422 on breach. */
const OWNER_FORMS_MAX_ITEMS = 100;

const chunk = <T>(items: Array<T>, size: number): Array<Array<T>> =>
  items.length <= size
    ? [items]
    : Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, (i + 1) * size));

/*
 * The submit-time check gets its own, much shorter budget than the mount read: every tenant waits on
 * this one, and on expiry it yields no ranges, which reads downstream as unbounded and submits.
 */
const SUBMIT_RANGE_CHECK_TIMEOUT_MS = 2_500;

const fetchOwnerFormRanges = (
  client: ReturnType<RootDependencies['apiClient']>,
  { buildingUuid, items }: AddOnRangeCheck,
  timeoutMs: number,
): Observable<Record<string, AddOnQuantityRange>> =>
  /*
   * Batched, not truncated — their DTO carries `@ArrayMaxSize(100)`. Slicing to fit would leave
   * every surplus product unbounded and silently submittable. Attach rows sit far below 100.
   */
  forkJoin(
    chunk(items, OWNER_FORMS_MAX_ITEMS).map(batch =>
      client.getOwnerForms({ buildingUuid, items: batch }).pipe(
        timeout(timeoutMs),
        /*
         * ⛔ EACH BATCH FAILS ALONE. `forkJoin` rejects the whole array on one error, which
         * would discard the ranges every other batch returned and leave those items unbounded.
         */
        catchError(() => of(null)),
      ),
    ),
  ).pipe(
    map(responses => {
      const groups: Array<OwnerForm> = responses.flatMap(batch => batch?.response?.data ?? []);

      /*
       * Flattened across every owner. The grouping matters to the form and not to a range: a
       * product resolves on exactly one owner per request, so two groups cannot disagree about
       * the same item.
       *
       * ⚠️ The key is lower-cased here and ONLY here. work-orders echoes the uuid in its own
       * stored casing, and the lookup side is `AddOn.id` off the catalog read; the two agree in
       * MySQL's collation and not in JS.
       */
      return Object.fromEntries(
        groups.flatMap(group =>
          (group.catalogItems ?? []).map(
            ({ catalogItemUuid, minQuantity, maxQuantity }) =>
              [catalogItemUuid.toLowerCase(), { minQuantity, maxQuantity }] as const,
          ),
        ),
      );
    }),
  );

/*
 * RES-1050 — the post-booking add-on submission.
 *
 * ⛔ Deliberately NOT routed through the cart. The cart resolves exactly one payment recipient
 * (`owner_type` / `owner_id`), so it cannot carry both the landlord's room money and a vendor's. This
 * selection is handed to the Service Request path instead, and the rows it writes are what wakes the
 * worker over CDC.
 */
export const submitBookingAddOnsEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.submitBookingAddOns.request)),
    withLatestFrom(state$),
    /*
     * ⭐ `exhaustMap`, not `switchMap`. This POST creates rows. `switchMap` would CANCEL an in-flight
     * request and start another, so a double-tap could leave the first write already applied while the
     * client believes only the second happened — and the add-on rows carry no uniqueness constraint yet,
     * so the duplicate would stand. `exhaustMap` ignores further submits until the current one settles.
     * The button is also disabled while submitting; this is the half a UI cannot guarantee.
     */
    exhaustMap(([{ payload }, state]) => {
      const client = apiClient(state);
      /* Deferred: a refused submission must not even construct the request, let alone send it. */
      const post$ = defer(() => client.submitBookingAddOns(payload)).pipe(
        map(() => actions.submitBookingAddOns.success()),
        catchError((error: Error) =>
          of(actions.submitBookingAddOns.failure({ error: transformErrorIntoSerializableError(error) })),
        ),
      );

      if (!payload.rangeCheck) {
        return post$;
      }

      return fetchOwnerFormRanges(client, payload.rangeCheck, SUBMIT_RANGE_CHECK_TIMEOUT_MS).pipe(
        mergeMap((ranges): Observable<RootAction> => {
          const violations = findQuantityViolations(payload.add_ons, ranges);

          return violations.length > 0
            ? of(actions.blockAddOnSubmission({ bookingId: payload.bookingId, violations, ranges }))
            : post$;
        }),
      );
    }),
  );

/*
 * RES-1057 — resolve which of the vendor's questions apply, then fetch them.
 *
 * TWO HOPS IN ONE EPIC because neither half is useful alone: the owner-forms read returns a form UUID,
 * not a definition, and a definition cannot be fetched without one. Splitting them would also split the
 * fail-open behaviour across two failure handlers.
 *
 * ⭐ `switchMap`, unlike the submit epic's `exhaustMap`, and the difference is the point: these are
 * idempotent READS keyed on the current selection, and the selection changes as the tenant adds and
 * removes. The LATEST answer is the only correct one, so an in-flight resolve for a superseded
 * selection must be abandoned rather than allowed to land after it.
 */
/*
 * ⛔ A BOUND, BECAUSE THE CTA WAITS ON THIS. The submit button is disabled while the resolve is
 * pending — otherwise a tenant taps through before their vendor's questions arrive and the vendor
 * gets no answers. That gate turns a read which never SETTLES into a tenant who has paid for a room
 * and cannot leave the page: a rejection fails open and releases the button, but a hang does not.
 *
 * Neither `ajax` nor this app's API client imposes a timeout, so the bound has to be here. Ten
 * seconds is generous for a slow mobile network and still far short of a tenant giving up; on expiry
 * this becomes an ordinary failure and takes the documented fail-open path — no form, booking
 * completes, vendor gets no answers.
 */
const RESOLVE_TIMEOUT_MS = 10_000;

export const resolveAddOnFormEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.resolveAddOnForm.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) =>
      apiClient(state)
        .getOwnerForms({ buildingUuid: payload.buildingUuid, items: payload.items })
        .pipe(
          switchMap(({ response }) => {
            const groups: Array<OwnerForm> = response?.data ?? [];
            /*
             * Deliverable 6. An item echoed back in NO group has an owner with no instance at this
             * building — precisely what the create rejects on. Diffing here turns a submission that
             * would fail four services away into one the tenant can correct now.
             */
            const echoed = new Set(groups.flatMap(group => group.catalogItemUuids));
            const unresolvedItemIds = payload.items
              .map(item => item.catalogItemUuid)
              .filter(catalogItemUuid => !echoed.has(catalogItemUuid));

            /*
             * ⚠️ One party per booking, enforced upstream, so there is at most one group — and its
             * `formUuid` is null whenever no form is attached. That is the COMMON case: most bookings
             * carry no vendor form at all, so this returns without a second request.
             */
            const templateUuid = groups.find(group => group.formUuid)?.formUuid ?? null;

            /* The identity travels back untouched, so the reducer can tell a current result from a late one. */
            const identity = { bookingId: payload.bookingId, requestKey: payload.requestKey };

            if (!templateUuid) {
              return of(
                actions.resolveAddOnForm.success({
                  templateUuid: null,
                  definition: null,
                  unresolvedItemIds,
                  ...identity,
                }),
              );
            }

            return apiClient(state)
              .getFormDefinition({ templateUuid, lang: payload.lang })
              .pipe(
                map(({ response: definitionResponse }): RootAction => {
                  const result: ResolveAddOnFormResult = {
                    templateUuid,
                    definition: definitionResponse?.data ?? null,
                    unresolvedItemIds,
                    ...identity,
                  };

                  return actions.resolveAddOnForm.success(result);
                }),
              );
          }),
          timeout(RESOLVE_TIMEOUT_MS),
          catchError((error: Error) =>
            of(actions.resolveAddOnForm.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        ),
    ),
  );

/*
 * RES-1316. One definition, by uuid. The step's own lane resolves which template applies; here the
 * stored row already names it, so only the second hop is needed.
 *
 * Always succeeds: a definition that will not load means the answers render unlabelled rather than
 * the section failing, which matches every other form read on this surface.
 */
export const readAddOnFormDefinitionEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.readAddOnFormDefinition.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) =>
      apiClient(state)
        .getFormDefinition({ templateUuid: payload.templateUuid, lang: payload.lang })
        .pipe(
          timeout(RESOLVE_TIMEOUT_MS),
          map(
            ({ response }): RootAction =>
              actions.readAddOnFormDefinition.success({
                templateUuid: payload.templateUuid,
                lang: payload.lang,
                definition: response?.data ?? null,
              }),
          ),
          /*
           * A failure writes nothing, so the next mount retries. Caching null here would be
           * indistinguishable from "this template has no definition" and would hide the answers for
           * the rest of the session, since resets deliberately preserve this cache.
           */
          catchError((error: Error) =>
            of(actions.readAddOnFormDefinition.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        ),
    ),
  );

/*
 * RES-1311. The per-product quantity range, from the same endpoint the form lane calls.
 *
 * ⛔ SEPARATE FROM `resolveAddOnFormEpic` ON PURPOSE, and it must stay that way. Two reasons, both
 * silent if ignored: the form lane's status gates the Continue button, so borrowing its actions would
 * block the CTA on a range read; and the payloads differ in a way that breaks the form. This one sends
 * the WHOLE MENU, so the response carries one group PER OWNER — while the form epic picks its template
 * with `groups.find(group => group.formUuid)`, which is only unambiguous because its own payload names
 * a single party. Feed a multi-owner response into that and it installs an arbitrary vendor's
 * questions.
 *
 * `switchMap`: only the latest menu is worth landing, and this read writes nothing.
 */
export const resolveAddOnRangesEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.resolveAddOnRanges.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) =>
      fetchOwnerFormRanges(apiClient(state), payload, RESOLVE_TIMEOUT_MS).pipe(
        map(
          (ranges): RootAction =>
            actions.resolveAddOnRanges.success({
              ranges,
              bookingId: payload.bookingId,
              requestKey: payload.requestKey,
            }),
        ),
        /*
         * Fails open, and this is now only the backstop — the per-batch `catchError` above absorbs a
         * single failed request, so reaching here means something outside them broke. An absent range
         * is read downstream as "unbounded, floor of one", the behaviour that shipped before this lane
         * existed, so a forms outage or a missing `work_order_requestor` role costs the bound rather
         * than the order.
         */
        catchError((error: Error) =>
          of(actions.resolveAddOnRanges.failure({ error: transformErrorIntoSerializableError(error) })),
        ),
      ),
    ),
  );

/* ACCESS-2591. `switchMap`: only the latest room and start is worth landing. */
export const getBookingAddOnsMenuEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getBookingAddOnsMenu.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      const { ownerType, ownerUuid, resourceUuid, getResourceOptions } = payload.params;

      return apiClient(state)
        .getResource(ownerType, ownerUuid, resourceUuid, getResourceOptions)
        .pipe(
          map(({ response }) =>
            actions.getBookingAddOnsMenu.success({ key: payload.key, resource: response.data.resource }),
          ),
          catchError((error: Error) =>
            of(
              actions.getBookingAddOnsMenu.failure({
                key: payload.key,
                error: transformErrorIntoSerializableError(error),
              }),
            ),
          ),
        );
    }),
  );
